import { pluralize } from '@aytm/app/stats/lib/util/helpers'
import { dropOffPct, pctBanded } from '@aytm/app/stats/page/data-quality-report/lib/format'
import { SECTION } from '@aytm/app/stats/page/data-quality-report/lib/sections'
import { SECTION_STATE } from '@aytm/app/stats/page/data-quality-report/lib/section-state'
import { fieldingComplete } from '@aytm/app/stats/page/data-quality-report/lib/survey-status'

// Shown in place of the subtitle while a section's data is still being collected.
export const FIELDING_SUBTITLE = 'In-fielding analysis is in progress...'

// Placeholder copy while the /dqr request is in flight — the real figures aren't known yet.
export const LOADING_SUBTITLE = 'Loading...'

// The request failed outright; the header carries a retry action alongside this copy.
export const LOAD_FAILED_SUBTITLE = "We couldn't load this data."

// Marketing has not supplied the destination yet; mirrors the sidebar's pending Data Quality Guide
// link. Swapping in the real URL is a one-line change here.
export const PV_LEARN_MORE_URL = 'https://aytm.com/community/paid-viewpoint'

// Per-section header subtitle. The numbers come from the section's /dqr data. PaidViewpoint and
// Survey Design carry no /dqr number, so their copy is static.
const BUILDERS = {
  [SECTION.PAID_VIEWPOINT]: () =>
    `Quality starts with trust. <a href="${PV_LEARN_MORE_URL}" target="_blank" rel="noopener noreferrer">Learn more</a> why PV is one of the highest quality panels out there.`,
  [SECTION.TRAFFIC_SENTINEL]: (d = {}) => {
    // Numbers come only from the AYTM-pushed sentinel pass-through; neutral copy until it lands.
    const s = d?.sentinel
    if (!s) return 'The Sentinel screens every entry attempt for duplicate and suspicious traffic.'
    const blocked = s.totalBlocked || 0
    const rate = pctBanded(s.dedupRate)
    return `We've blocked <strong>${blocked}</strong> duplicated or suspicious ${pluralize(
      blocked,
      'attempt',
      'attempts'
    )} to enter the survey${rate == null ? '' : ` (${rate}%)`}.`
  },
  // Data Centrifuge runs On-the-Go: its results populate while the survey fields and can still change
  // until the final run at fielding close. So the copy tracks the survey's own status — an in-progress
  // indicator (with the count so far) while still fielding, the completed account once fielding closes.
  [SECTION.DATA_CENTRIFUGE]: (d = {}, surveyStatus) => {
    const quarantineCount = d?.quarantineCount || 0
    const responses = pluralize(quarantineCount, 'response', 'responses')
    if (!fieldingComplete(surveyStatus)) {
      return 0 < quarantineCount
        ? `Deep, response-level analysis is in progress and has quarantined <strong>${quarantineCount}</strong> ${responses} so far. Numbers can change until fielding completes.`
        : 'Deep, response-level analysis is in progress as your survey fields. Numbers can change until fielding completes.'
    }
    // Completion is claimed only where a run is on record. Without one the absence of quarantined
    // responses says nothing, and asserting a finished analysis over it states a falsehood.
    if (!d?.dcLastRunTs) return null
    return 0 < quarantineCount
      ? `Deep, response-level analysis completed for your entire sample and quarantined <strong>${quarantineCount}</strong> ${responses}.`
      : 'Deep, response-level analysis completed for your entire sample.'
  },
  // Summarises the blocks the body actually renders, so the hidden expert-review block is out of
  // this total too — otherwise the header states a figure the visible body cannot account for.
  // Neither figure is ever stated as a bare zero: the two are unrelated (a survey can carry
  // rejections without Kudos or the reverse), so each clause rewords on its own and only both
  // zeroes collapse the pair into a single sentence. "yet" is always true here — unlike the Data
  // Centrifuge, Human Review has no fielding-bound end state after which nothing more can arrive.
  [SECTION.HUMAN_REVIEW]: (d = {}) => {
    const rejected = d.visibleRejectedCount || 0
    const kudos = d.kudosCount || 0
    if (!rejected && !kudos) return 'No manual rejections or Kudos on this survey yet.'
    const rejectedClause = rejected
      ? `<strong>${rejected}</strong> ${pluralize(rejected, 'response was', 'responses were')} rejected`
      : 'No manual rejections on this survey yet'
    const kudosClause = kudos
      ? `<strong>${kudos}</strong> ${pluralize(kudos, 'panelist was', 'panelists were')} given Kudos`
      : 'no Kudos on this survey yet'
    return `${rejectedClause}, and ${kudosClause}.`
  },
  // Counts people, not completion: a completion percentage next to a drop-off section read as the
  // figure the section is about, which it is not.
  [SECTION.SURVEY_PERFORMANCE]: (d = {}, surveyStatus) => {
    // Nothing placeable to count, so there is no honest figure to lead with. The header carries the
    // whole account while the section is collapsed, so it says why rather than falling silent.
    if (!d.dropOffPlaced) return 'Drop-off tracking is not available for this survey.'
    const dropped = d.dropOffCount || 0
    const line = `<strong>${dropped}</strong> ${pluralize(dropped, 'respondent', 'respondents')} dropped from your survey`
    // A share needs a settled denominator. While the survey is in field the count is the only honest
    // figure — the share would be read as final against a population still moving. No completion rate
    // on the payload withholds it for the same reason: the count stands on its own rather than
    // trailing an em dash in parentheses.
    if (!fieldingComplete(surveyStatus) || d.completionRate == null) return `${line}.`
    return `${line} <strong>(${dropOffPct(d.completionRate)})</strong>.`
  },
  // Survey Design is frontend-derived (not on /dqr); its score arrives via the section data prop.
  [SECTION.SURVEY_DESIGN]: (d = {}) =>
    d.score == null
      ? 'See your Survey Design Score and how you can improve it next time.'
      : `Your Survey Design Score is <strong>${d.score}</strong> out of 500. Please see how you can improve it next time.`
}

// A pending section shows a placeholder notice — loading while /dqr is in flight, the
// in-fielding notice while its data is still being collected; otherwise the data-derived subtitle.
export const sectionSubtitle = (section, state, data, surveyStatus) => {
  if (state === SECTION_STATE.FIELDING_IN_PROGRESS) return FIELDING_SUBTITLE
  if (state === SECTION_STATE.LOADING_DATA) return LOADING_SUBTITLE
  if (state === SECTION_STATE.LOAD_FAILED) return LOAD_FAILED_SUBTITLE

  return BUILDERS[section](data, surveyStatus)
}
